import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { NavIcon, type IconName } from '@l2u/ui/components/NavIcon.tsx';
import { setActiveStation } from '@l2u/ui/lib/activeStation.ts';
import {
  groupNav,
  navForRole,
  roleLabel,
  scopeSummaryLabel,
  type NavGroup,
} from '../lib/adminAccess.ts';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { buildIdentityLogoutUrl, shouldHandOffToIdentity } from '../lib/auth/identityLogout.ts';
import { useSessionTimeout } from '../lib/auth/useSessionTimeout.ts';
import type { SessionEndReason } from '../lib/auth/sessionTimeout.ts';
import { BRAND } from '@l2u/ui/lib/brand.ts';

// The email domain bound to central identity. AdminLogin reads the same value
// to decide whether to offer the SSO button at all, which is exactly why it is
// the right signal for deciding the sign-out hand-off: if SSO was offered to an
// address, central sign-in is what that address used.
const SSO_DOMAIN = import.meta.env.VITE_ADMIN_SSO_DOMAIN;

// Accent palette for the admin shell chrome (matches --brand-primary).
const ACCENT = '#4D148C';
const ACCENT_LIGHT = '#F3EBFA';

type NavRow = {
  to: string;
  label: string;
  icon: IconName;
  group: NavGroup;
  end?: boolean;
  /** Super admin only — hidden from reviewers and viewers (navForRole). */
  adminOnly?: boolean;
};

const NAV: NavRow[] = [
  { to: '/', label: 'Dashboard', icon: 'dashboard', group: 'overview', end: true },
  { to: '/vendors', label: 'Vendors', icon: 'building', group: 'operate' },
  { to: '/expiring-documents', label: 'Expiring documents', icon: 'inbox', group: 'operate' },
  {
    to: '/questionnaires',
    label: 'Questionnaires',
    icon: 'list-checks',
    group: 'configure',
    adminOnly: true,
  },
  { to: '/documents', label: 'Documents', icon: 'file-text', group: 'configure', adminOnly: true },
  { to: '/settings', label: 'Settings', icon: 'settings', group: 'admin', adminOnly: true },
];

function initials(name: string | undefined): string {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase() || 'U';
  const last = parts[parts.length - 1] ?? '';
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase() || 'U';
}

export function AdminShell() {
  const { user, session, loading, signOut, accessDenied } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Central sign-out hand-off.
  //
  // Clearing only this portal's Supabase session leaves the l2u-identity
  // session alive, so the next sign-in is silent and credential-free -- and,
  // because no new SAML assertion is issued, it re-spends whatever Okta group
  // claims were captured at the ORIGINAL sign-in. That is why an Okta group
  // change appears not to take effect: `custom_claims` is only rewritten at
  // POST /sso/saml/acs, which a surviving central session never triggers.
  //
  // AdminNoAccess.tsx has done this since the SSO cutover. The shell did not,
  // so the footer button and the session cap -- every NORMAL sign-out -- left
  // the central session behind.
  //
  // No `reason` is passed to identity: its vocabulary is a closed enum whose
  // only member is 'portal_access_denied', which is untrue for both paths here.
  // The portal's own 'expired' reason rides in returnTo instead, so /login can
  // still explain itself after the round trip. identity's allow-list matches on
  // ORIGIN, so the query string survives validation.
  //
  // Not everyone here has a central session to clear -- password admins on a
  // non-SSO domain do not -- so shouldHandOffToIdentity decides. It is
  // deliberately asymmetric and errs toward handing off; see its header for
  // why, and for why access_source is the wrong signal.
  //
  // The signals are read BEFORE signOut(), which clears both.
  //
  // Unconfigured VITE_IDENTITY_LOGOUT_URL returns null and we stay local: the
  // portal session still ends, which is the safe half of the job.
  const handleSignOut = useCallback(
    async (reason?: SessionEndReason) => {
      const returnPath = reason ? `/login?reason=${reason}` : '/login';
      const handOff = shouldHandOffToIdentity({
        ssoDomain: SSO_DOMAIN,
        email: user?.email ?? session?.user?.email,
        provider: session?.user?.app_metadata?.provider as string | undefined,
      });
      navigate(returnPath, { replace: true });
      // `reason` is set only by useSessionTimeout, never by the sign-out
      // button, so it is exactly the 'the user did not choose this' signal.
      await signOut({ automatic: !!reason });
      if (!handOff) return;
      const centralLogout = buildIdentityLogoutUrl(
        import.meta.env.VITE_IDENTITY_LOGOUT_URL,
        `${window.location.origin}${returnPath}`,
      );
      if (centralLogout) window.location.href = centralLogout;
    },
    [navigate, signOut, user, session],
  );

  useSessionTimeout({
    enabled: !!user,
    onExpire: (reason) => {
      void handleSignOut(reason);
    },
  });

  const queryClient = useQueryClient();
  // Task 17: the switcher is gone, so nothing can set or clear a station any
  // more. Clear any persisted selection once on mount — a stale stored value
  // would otherwise keep narrowing every vendor surface invisibly, with no
  // control left in the UI to reset it. The store and the request-header
  // plumbing are deliberately left in place (out of scope).
  useEffect(() => {
    setActiveStation(null);
    void queryClient.invalidateQueries();
  }, [queryClient]);

  if (loading) return <CenteredSpinner />;
  // Refused, not signed out. This MUST come before the `!user` guard: a refused
  // user has no `user` either, so falling through would send them to /login,
  // where signing in returns the same identity and refuses again.
  if (accessDenied) {
    return <Navigate to="/no-access" state={{ reason: accessDenied.reason }} replace />;
  }
  if (!user) {
    // Preserve search/hash: an invite/recovery link can land token fragments
    // (#access_token=…, ?code=…) on any path when Supabase falls back to its
    // Site URL. Dropping them here strands the user on a clean login screen;
    // carrying them through lets AdminLogin's forwarder redeem the link.
    return (
      <Navigate
        to={{ pathname: '/login', search: location.search, hash: location.hash }}
        state={{ from: location }}
        replace
      />
    );
  }

  const rows = groupNav(navForRole(NAV, user.role));

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#F8FAFC' }}>
      {/* ===== Sidebar ===== */}
      <aside
        className="relative flex w-[260px] shrink-0 flex-col bg-white"
        style={{ borderRight: '1px solid #E2E8F0' }}
      >
        {/* Locate2u brand logo + portal sublabel */}
        <div className="px-5 pb-4 pt-5" style={{ borderBottom: '1px solid #F1F5F9' }}>
          <a href="https://www.locate2u.com" target="_blank" rel="noopener noreferrer">
            <img src="/locate2u-logo.png" alt="Locate2u" className="h-8 w-auto" />
          </a>
          <div className="mt-2 text-[13px]" style={{ color: '#64748B' }}>
            Admin Portal
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {rows.map((row) => {
            if (row.kind === 'header') {
              return (
                <div
                  key={row.key}
                  className="font-mono-eyebrow px-3 pb-1.5 pt-4 text-[9.5px] tracking-[0.14em]"
                  style={{ color: '#94A3B8' }}
                >
                  {row.label.toUpperCase()}
                </div>
              );
            }
            const item = row.row;
            return (
              <NavLink
                key={row.key}
                to={item.to}
                end={item.end ?? false}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-[15px] transition ${
                    isActive ? 'font-semibold' : 'font-medium hover:bg-slate-50'
                  }`
                }
                style={({ isActive }) =>
                  isActive ? { background: ACCENT_LIGHT, color: ACCENT } : { color: '#64748B' }
                }
              >
                {({ isActive }) => (
                  <>
                    <NavIcon
                      name={item.icon}
                      size={20}
                      style={{ color: isActive ? ACCENT : '#94A3B8' }}
                    />
                    <span className="flex-1 text-left">{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Task 17: the Active Station switcher was removed. Per-admin
            assigned_stations scoping is unchanged and still applied server-side,
            so an admin simply sees all of their assigned stations at once. */}

        {/* Footer: real user + sign out */}
        <div className="px-3 pb-4 pt-3" style={{ borderTop: '1px solid #F1F5F9' }}>
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="flex w-full items-center gap-3 rounded-lg p-2 text-left transition hover:bg-slate-50"
            title="Sign out"
          >
            <div
              className="font-mono-eyebrow flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-bold"
              style={{ background: ACCENT_LIGHT, color: ACCENT }}
            >
              {initials(user.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold" style={{ color: '#1E293B' }}>
                {user.name}
              </div>
              <div className="truncate text-[11px]" style={{ color: '#64748B' }}>
                {user.email}
              </div>
              <div className="truncate text-[11px]" style={{ color: '#94A3B8' }}>
                {/* roleLabel, not an inline ternary: the old one captioned a
                    super admin as "Reviewer". scopeSummaryLabel reads the same
                    stored assignment as anyone else now that scope is
                    orthogonal to tier (D14). */}
                {roleLabel(user.role)} · {scopeSummaryLabel(user.assigned_stations, null)}
              </div>
            </div>
            <NavIcon name="log-out" size={16} style={{ color: '#94A3B8' }} />
          </button>
        </div>
      </aside>

      {/* ===== Main: top bar + content ===== */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top header bar: FedEx brand mark + "Powered by Locate2u" lockup */}
        <header
          className="flex items-center gap-3 bg-white px-8 py-4"
          style={{ borderBottom: '1px solid #E2E8F0' }}
        >
          <img src={BRAND.logoUrl} alt={BRAND.logoAlt} className="h-7 w-auto" />
          <span aria-hidden className="h-6 w-px bg-slate-200" />
          <span className="text-sm text-slate-500">
            Powered by{' '}
            <a
              href="https://www.locate2u.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium hover:underline"
              style={{ color: 'var(--brand-primary)' }}
            >
              Locate2u
            </a>
          </span>
        </header>

        {/* relative: the scroller must also be the containing block. Without it
            an absolute descendant (every answer pill's sr-only "(selected)")
            is placed against the document, and a long questionnaire gives
            <html> a second scrollbar beside this one. */}
        <main className="relative flex-1 overflow-y-auto" style={{ background: '#F8FAFC' }}>
          <div className="px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
